import React, { useState } from 'react';
import { Card, Typography, Row, Col } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { LocationData, DataStatus } from '../../services/DashboardService';
import { formatCurrency, formatPercent, formatPercentWithSign, getDisplayLocationName, formatDisplayDate } from '../../utils/formatters';
import { normalizeDate } from '../../utils/dateUtils';

const { Text } = Typography;

interface LocationCardProps {
  location: LocationData;
  isDMView: boolean;
  viewMode: 'weekly' | 'quarterly';
  dataStatus: DataStatus | null;
  startDate?: string;
  endDate?: string;
}

export const LocationCard: React.FC<LocationCardProps> = ({
  location,
  isDMView,
  viewMode,
  dataStatus,
  startDate,
  endDate,
}) => {
  const [showMore, setShowMore] = useState(false);
  const prefix = viewMode === 'quarterly' ? 'Qtr Weekly Avg' : '8-Wk Avg';

  // Calculate prior week date range for display (same logic as DashboardColumns)
  const priorWeekDateRangeText = endDate && startDate && viewMode === 'weekly'
    ? (() => {
        const end = normalizeDate(endDate);
        const start = normalizeDate(startDate);
        const dayOfWeek = end.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const isoWeekMonday = new Date(end);
        isoWeekMonday.setDate(isoWeekMonday.getDate() - daysToMonday);
        const weekStart = isoWeekMonday >= start ? isoWeekMonday : start;
        const formatDate = (date: Date): string => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        return `${formatDisplayDate(formatDate(weekStart))}-${formatDisplayDate(formatDate(end))}`;
      })()
    : null;

  const eightWeekPercentValue =
    location.eightWeekGfsPercentValue !== undefined && location.eightWeekGfsPercentValue !== null
      ? location.eightWeekGfsPercentValue
      : location.eightWeekTotalSales > 0 &&
          location.eightWeekTotalGfs !== null &&
          location.eightWeekTotalGfs !== undefined
        ? (location.eightWeekTotalGfs / location.eightWeekTotalSales) * 100
        : null;
  const eightWeekVarianceValue =
    location.eightWeekGfsVariance !== undefined ? location.eightWeekGfsVariance : null;
  const eightWeekVarianceColor =
    eightWeekVarianceValue !== null
      ? eightWeekVarianceValue <= -2
        ? '#52c41a'
        : eightWeekVarianceValue >= 2
          ? '#ff4d4f'
          : undefined
      : undefined;

  return (
    <Card
      size="small"
      style={{ marginBottom: 8 }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      {/* Header with location name */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: 15, display: 'block', lineHeight: 1.3 }}>
            {isDMView ? location.group : getDisplayLocationName(location.location)}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {isDMView ? getDisplayLocationName(location.location) : location.group}
          </Text>
        </div>
      </div>

      {/* Main metrics section */}
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
        {viewMode === 'weekly' ? (
          <>
            {/* PRIOR WEEK Section */}
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 8 }}>
                PRIOR WEEK {priorWeekDateRangeText && `(${priorWeekDateRangeText})`}
              </Text>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                backgroundColor: '#fafafa',
                padding: '10px 12px',
                borderRadius: 6,
              }}>
                <div style={{ textAlign: 'left' }}>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block', whiteSpace: 'nowrap' }}>Total Sales</Text>
                  <Text style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {location.eightWeekTotalSales > 0 ? formatCurrency(location.eightWeekTotalSales) : '--'}
                  </Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block', whiteSpace: 'nowrap' }}>GFS Spend</Text>
                  <Text style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {location.eightWeekTotalGfs ? formatCurrency(location.eightWeekTotalGfs) : '--'}
                  </Text>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block', whiteSpace: 'nowrap' }}>GFS Var</Text>
                  <Text style={{
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: eightWeekVarianceColor
                  }}>
                    {eightWeekVarianceValue !== null ? formatPercentWithSign(eightWeekVarianceValue) : '--'}
                  </Text>
                </div>
              </div>
            </div>

            {/* 8-WEEK AVERAGES Section */}
            <div style={{ marginBottom: showMore ? 12 : 0 }}>
              <Text strong style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 8 }}>
                8-WEEK AVERAGES
              </Text>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                backgroundColor: '#fafafa',
                padding: '10px 12px',
                borderRadius: 6,
              }}>
                <div style={{ textAlign: 'left' }}>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block', whiteSpace: 'nowrap' }}>Weekly Sales</Text>
                  <Text style={{
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: dataStatus && (!dataStatus.toastDataAvailable || !dataStatus.ezcaterDataAvailable) ? '#ff4d4f' : undefined,
                  }}>
                    {dataStatus && (!dataStatus.toastDataAvailable || !dataStatus.ezcaterDataAvailable)
                      ? '--' : formatCurrency(location.weeklyTotal)}
                  </Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block', whiteSpace: 'nowrap' }}>GFS Spend</Text>
                  <Text style={{
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: (dataStatus && !dataStatus.gfsDataAvailable) || !location.weeklyGfs ? '#ff4d4f' : undefined,
                  }}>
                    {dataStatus && !dataStatus.gfsDataAvailable ? '--' : location.weeklyGfs ? formatCurrency(location.weeklyGfs) : '--'}
                  </Text>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block', whiteSpace: 'nowrap' }}>GFS Var</Text>
                  <Text style={{
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: location.varianceFromAvg <= -2 ? '#52c41a' : location.varianceFromAvg >= 2 ? '#ff4d4f' : undefined,
                  }}>
                    {formatPercentWithSign(location.varianceFromAvg)}
                  </Text>
                </div>
              </div>
            </div>

            {/* Expanded details for weekly */}
            {showMore && (
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11 }}>8-Wk Avg Toast</Text>
                    <div style={{
                      fontWeight: 500,
                      color: dataStatus && !dataStatus.toastDataAvailable ? '#ff4d4f' : undefined,
                    }}>
                      {dataStatus && !dataStatus.toastDataAvailable ? '--' : formatCurrency(location.weeklySalesToast)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11 }}>8-Wk Avg Cater</Text>
                    <div style={{
                      fontWeight: 500,
                      color: (dataStatus && !dataStatus.ezcaterDataAvailable) || location.weeklyCatering === null ? '#ff4d4f' : undefined,
                    }}>
                      {(dataStatus && !dataStatus.ezcaterDataAvailable) || location.weeklyCatering === null
                        ? '--' : formatCurrency(location.weeklyCatering)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Prior Week GFS %</Text>
                    <div style={{ fontWeight: 500 }}>
                      {eightWeekPercentValue !== null ? formatPercent(eightWeekPercentValue) : '--'}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11 }}>8-Wk GFS %</Text>
                    <div style={{ fontWeight: 500 }}>
                      {location.gfsPercent !== null && location.gfsPercent > 0 ? formatPercent(location.gfsPercent) : '--'}
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Quarterly View Metrics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px 16px',
              paddingBottom: showMore ? 12 : 0,
              borderBottom: showMore ? '1px solid #f0f0f0' : 'none',
            }}>
              <div>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
                  Qtr Avg GFS %
                </Text>
                <Text strong style={{ fontSize: 18 }}>
                  {location.gfsPercent !== null && location.gfsPercent > 0 ? formatPercent(location.gfsPercent) : '--'}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
                  GFS % Prev Qtr Chg
                </Text>
                <Text strong style={{
                  fontSize: 18,
                  color: location.gfsPercentTrend !== null && location.gfsPercentTrend !== undefined
                    ? (location.gfsPercentTrend > 0 ? '#ff4d4f' : location.gfsPercentTrend < 0 ? '#52c41a' : undefined)
                    : undefined
                }}>
                  {location.gfsPercentTrend !== null && location.gfsPercentTrend !== undefined
                    ? formatPercentWithSign(location.gfsPercentTrend)
                    : '--'}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
                  Qtr Var from Avg
                </Text>
                <Text strong style={{
                  fontSize: 18,
                  color: location.varianceFromAvg <= -2 ? '#52c41a' : location.varianceFromAvg >= 2 ? '#ff4d4f' : undefined
                }}>
                  {formatPercentWithSign(location.varianceFromAvg)}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
                  Qtr Avg Weekly Sales
                </Text>
                <Text strong style={{ fontSize: 16 }}>
                  {formatCurrency(location.weeklyTotal)}
                </Text>
              </div>
            </div>

            {/* Expanded details for quarterly */}
            {showMore && (
              <Row gutter={[8, 8]} style={{ paddingTop: 12 }}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>{`${prefix} Toast`}</Text>
                  <div style={{ fontWeight: 500 }}>
                    {formatCurrency(location.weeklySalesToast)}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>{`${prefix} Cater`}</Text>
                  <div style={{ fontWeight: 500 }}>
                    {location.weeklyCatering !== null ? formatCurrency(location.weeklyCatering) : '--'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>{`${prefix} GFS Spend`}</Text>
                  <div style={{ fontWeight: 500 }}>
                    {location.weeklyGfs ? formatCurrency(location.weeklyGfs) : '--'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Total Sales</Text>
                  <div style={{ fontWeight: 500 }}>
                    {location.eightWeekTotalSales > 0 ? formatCurrency(location.eightWeekTotalSales) : '--'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Total Sales Prev Qtr Chg</Text>
                  <div style={{
                    fontWeight: 500,
                    color: location.totalSalesTrend !== null && location.totalSalesTrend !== undefined
                      ? (location.totalSalesTrend > 0 ? '#52c41a' : location.totalSalesTrend < 0 ? '#ff4d4f' : undefined)
                      : undefined
                  }}>
                    {location.totalSalesTrend !== null && location.totalSalesTrend !== undefined
                      ? formatCurrency(location.totalSalesTrend)
                      : '--'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Total GFS</Text>
                  <div style={{ fontWeight: 500 }}>
                    {location.eightWeekTotalGfs ? formatCurrency(location.eightWeekTotalGfs) : '--'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Total GFS Prev Qtr Chg</Text>
                  <div style={{
                    fontWeight: 500,
                    color: location.totalGfsTrend !== null && location.totalGfsTrend !== undefined
                      ? (location.totalGfsTrend > 0 ? '#ff4d4f' : location.totalGfsTrend < 0 ? '#52c41a' : undefined)
                      : undefined
                  }}>
                    {location.totalGfsTrend !== null && location.totalGfsTrend !== undefined
                      ? formatCurrency(location.totalGfsTrend)
                      : '--'}
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
      </div>

      {/* Show more/less toggle */}
      <div
        onClick={() => setShowMore(!showMore)}
        style={{
          textAlign: 'center',
          paddingTop: 8,
          cursor: 'pointer',
          color: '#1890ff',
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        {showMore ? (
          <>Less <UpOutlined style={{ fontSize: 10 }} /></>
        ) : (
          <>More <DownOutlined style={{ fontSize: 10 }} /></>
        )}
      </div>
    </Card>
  );
};
